import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import type { TaskState, TaskType } from '../types';
import { TaskRenderer } from '../rendering/TaskRenderer';
import { DifficultySystem } from './DifficultySystem';
import { PLATFORM } from '../constants';
import { Group } from 'three';

const TASK_TYPES: TaskType[] = [
  'color-sequence', 'shape-match', 'pattern-grid', 'signal-flow', 'symmetry'
];

let taskIdCounter = 0;

export class DispatchSystem {
  private state: GameState;
  private events: EventBus;
  private taskQueueGroup: Group;
  private spawnTimer = 0;

  constructor(state: GameState, events: EventBus, taskQueueGroup: Group) {
    this.state = state;
    this.events = events;
    this.taskQueueGroup = taskQueueGroup;
  }

  update(dt: number, time: number): void {
    if (this.state.phase !== 'playing') return;

    // Update difficulty
    this.state.difficulty = DifficultySystem.getDifficulty(time);

    // Spawn new tasks on timer
    this.spawnTimer += dt;
    if (this.spawnTimer >= this.state.difficulty.taskSpawnInterval) {
      this.spawnTimer = 0;
      this.trySpawnTask();
    }

    // Update existing tasks (urgency countdown, visuals)
    for (let i = this.state.taskQueue.length - 1; i >= 0; i--) {
      const task = this.state.taskQueue[i];
      task.urgency -= dt;

      // Update visuals
      if (task.mesh) {
        const group = task.mesh.parent as Group;
        if (group) {
          TaskRenderer.updateTaskVisuals(task, time);
        }
      }

      // Task expired
      if (task.urgency <= 0) {
        this.removeTask(i);
        this.state.tasksExpired++;
        this.state.updateDR();
        this.events.emit('task:expired', { taskId: task.id });
      }
    }

    // Update queue layout
    this.updateQueueLayout();
  }

  private trySpawnTask(): void {
    if (this.state.taskQueue.length >= this.state.difficulty.queueMaxSize) {
      // Queue overflow — lose a life!
      this.state.tasksExpired++;
      this.state.updateDR();
      this.events.emit('task:expired', { taskId: 'overflow' });
      return;
    }

    const type = TASK_TYPES[Math.floor(Math.random() * TASK_TYPES.length)];
    const isPriority = Math.random() < this.state.difficulty.priorityChance;
    const complexity = this.state.difficulty.taskComplexity;

    const task: TaskState = {
      id: `task_${++taskIdCounter}`,
      type,
      urgency: this.state.difficulty.taskTimeout,
      priority: isPriority,
      complexity,
      puzzleData: {
        reference: [],
        output: [],
        hasError: false,
      },
      mesh: null,
    };

    // Create 3D mesh
    const group = TaskRenderer.createTaskMesh(task);
    this.taskQueueGroup.add(group);
    // Store a reference — we use the card mesh inside the group
    const cardMesh = group.getObjectByName('card');
    if (cardMesh) {
      task.mesh = cardMesh as any;
    }

    this.state.taskQueue.push(task);
  }

  assignTaskToAgent(taskId: string, agentId: string): boolean {
    const taskIdx = this.state.taskQueue.findIndex(t => t.id === taskId);
    const agent = this.state.agents.find(a => a.id === agentId);

    if (taskIdx === -1 || !agent || agent.status !== 'idle') return false;

    const task = this.state.taskQueue[taskIdx];

    // Assign task to agent
    agent.currentTask = task;
    agent.status = 'working';
    agent.taskProgress = 0;

    // Remove from queue and scene
    this.removeTask(taskIdx);

    // Track KPI
    this.state.tasksAssigned++;
    this.state.updateDR();

    this.events.emit('task:assigned', { taskId, agentId });
    return true;
  }

  private removeTask(index: number): void {
    const task = this.state.taskQueue[index];
    if (task.mesh) {
      const group = task.mesh.parent;
      if (group) {
        this.taskQueueGroup.remove(group);
        group.traverse(child => {
          if ('geometry' in child && child.geometry) {
            (child as any).geometry.dispose();
          }
          if ('material' in child && child.material) {
            const mat = child.material;
            if (Array.isArray(mat)) mat.forEach(m => m.dispose());
            else (mat as any).dispose();
          }
        });
      }
    }
    this.state.taskQueue.splice(index, 1);
  }

  private updateQueueLayout(): void {
    const tasks = this.state.taskQueue;
    const count = tasks.length;
    if (count === 0) return;

    // Arrange tasks in an arc on the opposite side of the platform from agents
    const arcRadius = PLATFORM.radius + 1.5;
    const arcSpan = Math.PI * 0.8;
    const startAngle = Math.PI * 0.6;

    for (let i = 0; i < count; i++) {
      const task = tasks[i];
      if (!task.mesh) continue;

      const group = task.mesh.parent;
      if (!group) continue;

      const t = count > 1 ? i / (count - 1) : 0.5;
      const angle = startAngle + t * arcSpan;

      const targetX = Math.cos(angle) * arcRadius;
      const targetZ = Math.sin(angle) * arcRadius;

      // Smooth lerp to position
      group.position.x += (targetX - group.position.x) * 0.05;
      group.position.z += (targetZ - group.position.z) * 0.05;

      // Face the center
      const lookAngle = Math.atan2(-group.position.x, -group.position.z);
      group.rotation.y = lookAngle;
    }
  }

  reset(): void {
    // Pre-load the spawn timer so first task comes fast (~1.5s)
    this.spawnTimer = 5.5; // interval is 7 at start, so 7 - 5.5 = 1.5s until first spawn

    // Remove all task meshes
    while (this.taskQueueGroup.children.length > 0) {
      const child = this.taskQueueGroup.children[0];
      this.taskQueueGroup.remove(child);
      child.traverse(c => {
        if ('geometry' in c && c.geometry) (c as any).geometry.dispose();
        if ('material' in c && c.material) {
          const mat = c.material;
          if (Array.isArray(mat)) mat.forEach(m => m.dispose());
          else (mat as any).dispose();
        }
      });
    }
    this.state.taskQueue = [];
    // spawnTimer stays at 5.5 (set above) so first task comes in ~1.5s
    taskIdCounter = 0;
  }
}
